/**
 * EditJobDialog — Edit-existing-job modal (extracted Feb 2026 from JobsEnhanced.jsx).
 *
 * Mirror of AddJobDialog with edit-mode semantics: prefilled values, no
 * customer-inline-create, project-number typeahead via /api/projects/search,
 * and a "Save & Edit Next" companion button.
 *
 * Pure presentational — all state + handlers live in the parent.
 */
import React from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { MapPin, Camera, Clock, AlertCircle } from 'lucide-react';
import { COUNTRIES, getStatesForCountry, getPostalCodeLabel, getStateLabel } from '../../data/countries';
import WorkerMultiSelect from './WorkerMultiSelect';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

export default function EditJobDialog({
  open,
  onOpenChange,
  // Form state
  editJob, setEditJob,
  users, customers,
  // Project typeahead state
  editProjOpen, setEditProjOpen,
  editProjResults, setEditProjResults,
  // Submit
  onSubmit, isLoading, jobsCount,
  // Auth
  token,
}) {
  if (!editJob) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-3 border-b">
          <DialogTitle className="text-xl sm:text-2xl font-bold text-blue-900">Edit Job</DialogTitle>
          <DialogDescription className="text-sm">
            Update job details and assignment (Admin only)
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="edit-job-scroll-area">
            <div className="space-y-2">
              <Label htmlFor="edit_title">Job Title *</Label>
              <Input
                id="edit_title"
                placeholder="Enter job title"
                value={editJob.title}
                onChange={(e) => setEditJob({ ...editJob, title: e.target.value })}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_description">Description</Label>
              <Textarea
                id="edit_description"
                placeholder="Job details"
                value={editJob.description ?? ''}
                onChange={(e) => setEditJob({ ...editJob, description: e.target.value })}
                rows={3}
                className="resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label>Assigned worker(s) *</Label>
              {editJob.assigned_team_id && (editJob.assigned_user_ids || []).length === 0 && (
                <p className="text-xs text-amber-300" data-testid="edit-job-team-note">This job is assigned to a team/squad. Picking workers here switches it to individual assignment.</p>
              )}
              <WorkerMultiSelect
                users={users}
                value={editJob.assigned_user_ids || (editJob.assigned_to ? [editJob.assigned_to] : [])}
                onChange={(ids) => setEditJob({ ...editJob, assigned_user_ids: ids, assigned_to: ids[0] || editJob.assigned_to })}
                testId="edit-job-workers"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_customer_select">Customer <span className="text-red-500">*</span></Label>
              <Select
                value={editJob.customer_id || ''}
                onValueChange={(value) => {
                  const cust = customers.find((c) => c.id === value);
                  setEditJob({
                    ...editJob,
                    customer_id: value,
                    customer_name: cust?.name || editJob.customer_name || '',
                    customer_email: cust?.email || editJob.customer_email || '',
                  });
                }}
                required
              >
                <SelectTrigger className="h-11" data-testid="edit-customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editJob.customer_name && !editJob.customer_id && (
                <p className="text-xs text-amber-400">
                  Legacy customer &quot;{editJob.customer_name}&quot; — please pick a customer record.
                </p>
              )}
            </div>

            {/* Project picker (Feb 2026 — QuickBooks parity). */}
            <div className="space-y-2 relative">
              <Label htmlFor="edit_project_number">Project Number</Label>
              <Input
                id="edit_project_number"
                data-testid="edit-project-number"
                autoComplete="off"
                placeholder="e.g. 2025060 — type to search or enter freely"
                value={
                  editJob.project_number
                    ? (editJob.project_name
                        ? `${editJob.project_number} — ${editJob.project_name}`
                        : editJob.project_number)
                    : ''
                }
                onFocus={async () => {
                  setEditProjOpen(true);
                  try {
                    const { data } = await axios.get(
                      `${API}/projects/search?q=&limit=20`,
                      { headers: { Authorization: `Bearer ${token}` } },
                    );
                    setEditProjResults(Array.isArray(data) ? data : []);
                  } catch { setEditProjResults([]); }
                }}
                onBlur={() => setTimeout(() => setEditProjOpen(false), 150)}
                onChange={async (e) => {
                  const raw = e.target.value;
                  setEditJob({
                    ...editJob,
                    project_id: '',
                    project_number: raw,
                    project_name: '',
                  });
                  setEditProjOpen(true);
                  const q = raw.split(' ')[0].trim();
                  try {
                    const { data } = await axios.get(
                      `${API}/projects/search?q=${encodeURIComponent(q)}&limit=20`,
                      { headers: { Authorization: `Bearer ${token}` } },
                    );
                    setEditProjResults(Array.isArray(data) ? data : []);
                  } catch { setEditProjResults([]); }
                }}
                className="h-11"
              />
              {editJob.project_number && (
                <button
                  type="button"
                  data-testid="edit-project-clear"
                  onClick={() => setEditJob({ ...editJob, project_id: '', project_number: '', project_name: '' })}
                  className="absolute right-3 top-9 text-xs text-slate-400 hover:text-slate-200"
                >
                  Clear
                </button>
              )}
              {editProjOpen && editProjResults.length > 0 && (
                <div
                  data-testid="edit-project-results"
                  className="absolute z-50 left-0 right-0 mt-1 bg-[#1A2433] border border-[#2D3748] rounded-md shadow-lg max-h-56 overflow-y-auto"
                >
                  {editProjResults.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`edit-project-pick-${p.id}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setEditJob({
                          ...editJob,
                          project_id: p.id,
                          project_number: p.project_number,
                          project_name: p.name,
                        });
                        setEditProjOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-[#0F1923] border-b last:border-b-0 border-[#2D3748]"
                    >
                      <div className="font-mono text-xs text-emerald-400">{p.project_number}</div>
                      <div className="text-sm text-white">{p.name}</div>
                      {p.customer_name && (
                        <div className="text-[11px] text-slate-400">{p.customer_name}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-slate-500">
                Tip: pick a project to keep things linked to QuickBooks. If you only know the number, just type it — we&apos;ll match it on save when possible.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_scheduled_start">Scheduled Start <span className="text-red-500">*</span></Label>
                <Input
                  id="edit_scheduled_start"
                  type="datetime-local"
                  value={editJob.scheduled_start || ''}
                  onChange={(e) => setEditJob({ ...editJob, scheduled_start: e.target.value })}
                  className="h-11"
                  required
                  data-testid="edit-scheduled-start"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_scheduled_completion">Scheduled Completion <span className="text-red-500">*</span></Label>
                <Input
                  id="edit_scheduled_completion"
                  type="datetime-local"
                  value={editJob.scheduled_completion || ''}
                  onChange={(e) => setEditJob({ ...editJob, scheduled_completion: e.target.value })}
                  className="h-11"
                  required
                  data-testid="edit-scheduled-completion"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_service_rate">Billing Rate ($/hr) <span className="text-red-500">*</span></Label>
              <Input
                id="edit_service_rate"
                type="number"
                step="0.01"
                min="0"
                placeholder="e.g. 65"
                value={editJob.service_rate ?? ''}
                onChange={(e) => setEditJob({ ...editJob, service_rate: e.target.value })}
                className="h-11"
                required
                data-testid="edit-service-rate"
              />
            </div>

            {/* Budgeted hours (Feb 2026 — payroll workbench). Optional cap
                used to flag over-budget workers and prevent run-on shifts
                on per-job basis. Empty / 0 clears the cap. */}
            <div className="space-y-2">
              <Label htmlFor="edit_budgeted_hours">Budgeted Hours (optional cap)</Label>
              <Input
                id="edit_budgeted_hours"
                type="number"
                step="0.25"
                min="0"
                max="1000"
                placeholder="e.g. 4"
                value={editJob.budgeted_hours ?? ''}
                onChange={(e) => setEditJob({ ...editJob, budgeted_hours: e.target.value })}
                className="h-11"
                data-testid="edit-budgeted-hours"
              />
              <p className="text-xs text-gray-500">
                Sets a soft cap on total hours a worker should log on this job. The Payroll Report flags entries that exceed the cap so admins can adjust before pushing to QuickBooks. Leave blank for no cap.
              </p>
            </div>

            {/* Customer reference numbers (Feb 2026 — Review & Push). Flow
                to QuickBooks: PO # → native Invoice.PONumber; Work Order #
                → PrivateNote when included in the push dialog. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_customer_po_number">PO Number (from customer)</Label>
                <Input
                  id="edit_customer_po_number"
                  placeholder="e.g. PO-44512"
                  maxLength={25}
                  value={editJob.customer_po_number ?? ''}
                  onChange={(e) => setEditJob({ ...editJob, customer_po_number: e.target.value })}
                  className="h-11"
                  data-testid="edit-po-number"
                />
                <p className="text-xs text-gray-500">Customer-visible on QBO invoice.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_work_order_number">Work Order #</Label>
                <Input
                  id="edit_work_order_number"
                  placeholder="e.g. WO-7788"
                  maxLength={64}
                  value={editJob.work_order_number ?? ''}
                  onChange={(e) => setEditJob({ ...editJob, work_order_number: e.target.value })}
                  className="h-11"
                  data-testid="edit-work-order"
                />
                <p className="text-xs text-gray-500">Included in QBO invoice memo when pushed.</p>
              </div>
            </div>

            {/* Structured Address Fields */}
            <div className="space-y-4 p-4 bg-[#0F1923] rounded-lg border border-[#2D3748]">
              <div className="flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-[#1E88E5]" />
                <Label className="text-sm font-semibold text-blue-900">
                  Job Site Address
                </Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_street_address">Street Address *</Label>
                <Input
                  id="edit_street_address"
                  placeholder="e.g., 123 Main Street"
                  value={editJob.street_address}
                  onChange={(e) => setEditJob({ ...editJob, street_address: e.target.value })}
                  className="h-11"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_city">City *</Label>
                  <Input
                    id="edit_city"
                    placeholder="City"
                    value={editJob.city}
                    onChange={(e) => setEditJob({ ...editJob, city: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_country">Country *</Label>
                  <Select
                    value={editJob.country}
                    onValueChange={(value) => setEditJob({ ...editJob, country: value, state_province: '' })}
                    required
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_state_province">{getStateLabel(editJob.country)} *</Label>
                  <Select
                    value={editJob.state_province}
                    onValueChange={(value) => setEditJob({ ...editJob, state_province: value })}
                    required
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={`Select ${getStateLabel(editJob.country).toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {getStatesForCountry(editJob.country).map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_postal_code">{getPostalCodeLabel(editJob.country)} *</Label>
                  <Input
                    id="edit_postal_code"
                    placeholder={getPostalCodeLabel(editJob.country)}
                    value={editJob.postal_code}
                    onChange={(e) => setEditJob({ ...editJob, postal_code: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>
              </div>

              <p className="text-xs text-blue-700">
                Location will be re-geocoded for GPS verification.
              </p>
            </div>

            {/* GPS Verification toggle — admins can flip this on/off any time. */}
            <div className="flex items-center space-x-3 p-4 bg-[#0F1923] rounded-lg border border-[#FFA000]">
              <input
                type="checkbox"
                id="edit_location_verification"
                data-testid="edit-job-gps-toggle"
                checked={editJob.location_verification_required || false}
                onChange={(e) => setEditJob({ ...editJob, location_verification_required: e.target.checked })}
                className="w-5 h-5 rounded border-[#2D3748] text-[#1E88E5] focus:ring-blue-500"
              />
              <div className="flex-1">
                <Label htmlFor="edit_location_verification" className="text-sm font-semibold text-yellow-900 cursor-pointer">
                  Require GPS Location Verification
                </Label>
                <p className="text-xs text-yellow-700 mt-1">
                  {editJob.location_verification_required
                    ? 'Worker must be within the geofence radius of the job site to start work.'
                    : 'Worker can start job from anywhere (useful for office work, admin tasks, or when location is not critical).'}
                </p>
              </div>
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            </div>

            {/* Per-job geofence radius override (Feb 2026). Empty → company
                default (500 m). Useful for campus jobs (5000+ m) or tight
                urban sites (100 m). */}
            {editJob.location_verification_required && (
              <div className="p-4 bg-[#0F1923] rounded-lg border border-[#2D3748]">
                <Label htmlFor="edit_geofence_radius" className="text-sm font-semibold text-slate-200">
                  Geofence radius (meters)
                </Label>
                <input
                  type="number"
                  id="edit_geofence_radius"
                  data-testid="edit-job-geofence-radius"
                  min="25"
                  max="50000"
                  step="25"
                  placeholder="500 (company default)"
                  value={editJob.geofence_radius_meters ?? ''}
                  onChange={(e) => setEditJob({ ...editJob, geofence_radius_meters: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0) })}
                  className="mt-2 w-full px-3 py-2 rounded bg-[#1A2433] border border-[#2D3748] text-slate-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <p className="text-xs text-slate-400 mt-2">
                  How close (in meters) the worker must be to the job site to start without a Travel Time override. Leave blank for the company default (500 m). Use a wider radius for campuses, a tighter one for residential addresses. Min 25 m, max 50,000 m.
                </p>
              </div>
            )}

            <div className="flex items-center space-x-3 p-4 bg-[#0F1923] rounded-lg border border-orange-200">
              <input
                type="checkbox"
                id="edit_photos_required"
                checked={editJob.photos_required}
                onChange={(e) => setEditJob({ ...editJob, photos_required: e.target.checked })}
                className="w-5 h-5 rounded border-[#2D3748] text-[#FFA000] focus:ring-orange-500"
              />
              <div className="flex-1">
                <Label htmlFor="edit_photos_required" className="text-sm font-semibold text-orange-900 cursor-pointer">
                  Photos Required
                </Label>
                <p className="text-xs text-orange-700 mt-1">
                  User must upload at least one photo before completing this job
                </p>
              </div>
              <Camera className="w-6 h-6 text-[#FFA000]" />
            </div>


          <div className="flex items-center space-x-3 p-4 bg-[#0F1923] rounded-lg" style={{ border: '1px solid rgba(0,200,150,0.35)' }}>
            <input
              type="checkbox"
              id="edit_billable"
              data-testid="edit-job-billable-toggle"
              checked={editJob.billable !== false}
              onChange={(e) => setEditJob({ ...editJob, billable: e.target.checked })}
              className="w-5 h-5 rounded border-[#2D3748]"
              style={{ accentColor: '#00C896' }}
            />
            <div className="flex-1">
              <Label htmlFor="edit_billable" className="text-sm font-semibold cursor-pointer" style={{ color: '#6EE7B7' }}>
                Billable job
              </Label>
              <p className="text-xs mt-1" style={{ color: '#64748B' }}>
                Untick for internal or warranty work. Non-billable hours count as overhead in the Service Rate Calculator.
              </p>
            </div>
          </div>
            <div className="flex items-center space-x-3 p-4 bg-[#0F1923] rounded-lg" style={{ border: '1px solid rgba(139,92,246,0.35)' }}>
              <input
                type="checkbox"
                id="edit_is_extended"
                data-testid="edit-job-extended-toggle"
                checked={editJob.is_extended || false}
                onChange={(e) => setEditJob({ ...editJob, is_extended: e.target.checked })}
                className="w-5 h-5 rounded border-[#2D3748] focus:ring-purple-500"
                style={{ accentColor: '#8B5CF6' }}
              />
              <div className="flex-1">
                <Label htmlFor="edit_is_extended" className="text-sm font-semibold cursor-pointer" style={{ color: '#C4B5FD' }}>
                  Extended Job (Multi-Day)
                </Label>
                <p className="text-xs mt-1" style={{ color: '#A78BFA' }}>
                  Disables auto-clock-out for workers on this job. Useful for jobs that span multiple shifts or days.
                </p>
              </div>
              <Clock className="w-6 h-6" style={{ color: '#8B5CF6' }} />
            </div>
          </div>

          <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2 px-6 py-4 border-t bg-inherit" data-testid="edit-job-footer">
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-[#1E88E5] hover:bg-blue-700 h-11"
              data-testid="edit-job-update-btn"
            >
              {isLoading ? 'Updating...' : 'Update Job'}
            </Button>
            <Button
              type="button"
              onClick={() => onSubmit(null, true)}
              disabled={isLoading || jobsCount <= 1}
              className="flex-1 bg-[#00C896] hover:bg-green-700 text-white h-11"
              title={jobsCount <= 1 ? 'No other jobs to edit' : 'Save current changes and open the next job for editing'}
              data-testid="edit-job-save-and-next-btn"
            >
              {isLoading ? 'Saving...' : 'Save & Edit Next'}
            </Button>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="flex-1 h-11"
              data-testid="edit-job-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
